import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import parser from 'html-react-parser';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { translate } from '../Services/translation';
import { FileUploader } from './FileUploader';
import { isPhoneNumberValid } from '../Helpers/isPhoneNumberValid';

const CompanyForm = (props) => {
  const { googleReCaptchaProps, descriptionHtml } = props;
  const { executeRecaptcha } = googleReCaptchaProps;

  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [telInputReady, setTelInputReady] = useState(false);

  const telInputRef = useRef();

  // POST request to Litium API
  const handleOfferRequest = (formFields, files) => {
    const requestParameters = JSON.stringify({ ...formFields });
    setRequestSuccess(false);
    setRequestError(false);
    setRequestLoading(true);

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    formData.append('request', requestParameters);

    const sunpanelRequestHeader = new Headers();
    sunpanelRequestHeader.append(
      'litium-request-context',
      `{'ChannelSystemId':'${window.__litium.requestContext.channelSystemId}'}`
    );

    fetch('/api/sunpanel/companyform/sendrequest', {
      method: 'POST',
      headers: sunpanelRequestHeader,
      body: formData,
      redirect: 'follow',
    })
      .then((response) => {
        if (response.status === 200) {
          setRequestSuccess(true);
        } else {
          setRequestError(true);
        }
      })
      .catch((error) => {
        setRequestError(true);
        console.error('Error:', error);
      })
      .finally(() => {
        setRequestLoading(false);
      });
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const elements = e.target.elements;

    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return;
    }

    executeRecaptcha('sunpanelOfferRequest').then((token) => {
      if (elements) {
        const companyName = elements.COMPANY_NAME.value;
        const address = elements.ADDRESS.value;
        const contactPerson = elements.CONTACT_PERSON.value;
        const ePost = elements.E_POST.value;
        const phone = elements.PHONE.value;
        const annualElectricityConsumption =
          elements.ANNUAL_ELECTRICITY_CONSUMPTION.value;
        const itOrTnGrid = elements.IT_OR_TN_GRID.checked;
        const sizeMainFuse = elements.SIZE_MAIN_FUSE.value;
        const typeTaktekke = elements.TYPE_TAKTEKKE.value;
        const takvinkel = elements.TAKVINKEL.value;
        const dimensionedForAdditionalLoads =
          elements.DIMENSIONED_FOR_ADDITIONAL_LOADS.checked;
        const heightBuilding = elements.HEIGHT_BUILDING.value;
        const inputFiles = elements.FILES.files;

        const obj = {
          name: companyName,
          address,
          contactPerson,
          email: ePost,
          phone,
          annualElectricityConsumption,
          itOrTnGrid,
          sizeMainFuse,
          typeTaktekke,
          takvinkel,
          additionalLoads: dimensionedForAdditionalLoads,
          height: heightBuilding,
          token,
        };

        handleOfferRequest(obj, inputFiles);
      }
    });
  };

  // Handle Phone input validation
  const onTelInput = (e) => {
    e.target.setCustomValidity('');
    const isValid = isPhoneNumberValid(e.target.value);
    if (!isValid) {
      e.target.setCustomValidity(translate('companyForm.action.phoneValidation'));
      e.target.reportValidity();
    }
  };

  useEffect(() => {
    if (telInputReady) {
      telInputRef.current.addEventListener('input', onTelInput);
    }

    return () => {
      telInputRef.current.removeEventListener('input', onTelInput);
    };
  }, [telInputReady]);

  const showFDBK = requestLoading || requestSuccess || requestError;
  const showForm = !requestSuccess && !requestError;
  const buttonDisabled = requestLoading;

  const shouldTryAgain = requestError && !requestLoading;
  const shouldResetForm = requestSuccess && !requestLoading;

  const tryAgainLabel = translate('companyForm.action.tryAgain');
  const resetFormLabel = translate('companyForm.action.resetForm');
  const submitLabel = translate('companyForm.action.submit');

  const ButtonLabel = (
    <>
      {buttonDisabled && (
        <span className="spinner">{translate('general.loading')}</span>
      )}
      {shouldTryAgain ? tryAgainLabel : null}
      {shouldResetForm ? resetFormLabel : null}
      {!buttonDisabled && !shouldTryAgain && !shouldResetForm
        ? submitLabel
        : null}
    </>
  );

  /**
   * Reset form state.
   * Used for "Try again" and "Reset form" buttons.
   */
  const resetForm = () => {
    setRequestSuccess(false);
    setRequestError(false);
  };

  return (
    <form
      id="sunpanel-offer-request-form"
      encType="multipart/form-data"
      name="sunpanel-offer-request-form"
      className="validate"
      onSubmit={handleSubmit}
    >
      <div>
        <div className="row">
          <div className="small-12">
            <h4 className="align-center">{translate('companyForm.Heading')}</h4>

            {descriptionHtml && showForm && (
              <div className="padding-left-1 padding-right-1 padding-bottom-2 padding-top-1 sunpanel-offer-request-form__description">
                {parser(descriptionHtml)}
              </div>
            )}

            <div
              className={`sunpanel-offer-request-form__fdbk-state${
                showFDBK
                  ? ' sunpanel-offer-request-form__fdbk-state--visible'
                  : ''
              } padding-horizontal-2 padding-vertical-3`}
            >
              {requestLoading ? (
                <span className="sunpanel-offer-request-form__fdbk-state--loading">
                  {translate('general.loading')}
                </span>
              ) : null}
              {requestSuccess ? (
                <span className="sunpanel-offer-request-form__fdbk-state--success">
                  {translate('companyForm.message.success')}
                </span>
              ) : null}
              {requestError ? (
                <span className="sunpanel-offer-request-form__fdbk-state--error">
                  {translate('companyForm.message.error')}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {showForm ? (
          <div className="row">
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-company-name"
                required={true}
              >
                {translate('companyForm.label.companyName')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="COMPANY_NAME"
                id="sunpanel-offer-company-name"
                placeholder={translate('companyForm.label.companyName')}
                required
                type="text"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-address"
                required={true}
              >
                {translate('companyForm.label.address')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="ADDRESS"
                id="sunpanel-offer-address"
                placeholder={translate('companyForm.label.address')}
                required
                type="text"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-contact-person"
                required={true}
              >
                {translate('companyForm.label.contactPerson')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="CONTACT_PERSON"
                id="sunpanel-offer-contact-person"
                placeholder={translate('companyForm.label.contactPerson')}
                required
                type="text"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-e-post"
                required={true}
              >
                {translate('companyForm.label.email')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="E_POST"
                id="sunpanel-offer-e-post"
                placeholder={translate('companyForm.label.email')}
                required
                type="email"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label className="form__label" htmlFor="sunpanel-offer-phone">
                {translate('companyForm.label.phone')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="PHONE"
                id="sunpanel-offer-phone"
                placeholder={translate('companyForm.label.phone')}
                type="tel"
                required
                ref={(node) => {
                  if (node) {
                    telInputRef.current = node;
                    setTelInputReady(true);
                  }
                }}
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-annual-electricity-consumption"
              >
                {translate('companyForm.label.annualConsumption')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="ANNUAL_ELECTRICITY_CONSUMPTION"
                id="sunpanel-offer-annual-electricity-consumption"
                placeholder={translate('companyForm.label.annualConsumption')}
                type="number"
                min={0}
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <div className="form__label">
                {translate('companyForm.label.grid')}
              </div>
              <div className="switch small form--inline-block">
                <input
                  className="switch-input"
                  id="sunpanel-offer-it-or-tn"
                  type="checkbox"
                  name="IT_OR_TN_GRID"
                />
                <label
                  className="switch-paddle"
                  htmlFor="sunpanel-offer-it-or-tn"
                >
                  <span className="switch-active" aria-hidden="true">
                    IT
                  </span>
                  <span className="switch-inactive" aria-hidden="true">
                    TN
                  </span>
                </label>
              </div>
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-size-main-fuse"
              >
                {translate('companyForm.label.mainFuseSize')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="SIZE_MAIN_FUSE"
                id="sunpanel-offer-size-main-fuse"
                placeholder={translate('companyForm.label.mainFuseSize')}
                type="number"
                min={0}
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-type-taktekke"
              >
                {translate('companyForm.label.roofType')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="TYPE_TAKTEKKE"
                id="sunpanel-offer-type-taktekke"
                placeholder={translate('companyForm.label.roofType')}
                type="text"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label className="form__label" htmlFor="sunpanel-offer-takvinkel">
                {translate('companyForm.label.roofAngle')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="TAKVINKEL"
                id="sunpanel-offer-takvinkel"
                placeholder={translate('companyForm.label.roofAngle')}
                type="text"
              />
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <div className="form__label">
                {translate('companyForm.label.additionalLoad')}
              </div>
              <div className="switch small">
                <input
                  className="switch-input"
                  id="sunpanel-offer-dimensioned-for-additional-loads"
                  type="checkbox"
                  name="DIMENSIONED_FOR_ADDITIONAL_LOADS"
                />
                <label
                  className="switch-paddle"
                  htmlFor="sunpanel-offer-dimensioned-for-additional-loads"
                >
                  <span className="switch-active" aria-hidden="true">
                    {translate('companyForm.label.yes')}
                  </span>
                  <span className="switch-inactive" aria-hidden="true">
                    {translate('companyForm.label.no')}
                  </span>
                </label>
              </div>
            </div>
            <div className="small-12 medium-6 padding-left-1 padding-right-1">
              <label
                className="form__label"
                htmlFor="sunpanel-offer-height-building"
              >
                {translate('companyForm.label.buildingHeight')}
              </label>
              <input
                className="sunpanel-offer-request-form__field"
                name="HEIGHT_BUILDING"
                id="sunpanel-offer-height-building"
                placeholder={translate('companyForm.label.buildingHeight')}
                type="number"
                min={0}
              />
            </div>

            <div className="padding-left-1">
              <FileUploader
                buttonLabel={translate('companyForm.action.upload')}
                fileSizeError={translate('companyForm.message.error.fileSize')}
                maxFilesError={translate('companyForm.message.error.maxFiles')}
                totalFileSizeError={translate(
                  'companyForm.message.error.totalFileSize'
                )}
                inputId="sunpanel-offer-file"
                inputName="FILES"
                maxFiles={10}
                fileSizeLimit="10MB"
                totalFileSizeLimit="10MB"
              />
            </div>
          </div>
        ) : null}

        <div
          className={`padding-2 align-center ${!showForm ? 'padding-top-3' : ''}`}
        >
          <button
            type={showForm ? 'submit' : 'button'}
            className="button"
            disabled={buttonDisabled}
            onClick={showForm ? undefined : () => resetForm()}
          >
            {ButtonLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default withGoogleReCaptcha(CompanyForm);

CompanyForm.propTypes = {
  googleReCaptchaProps: PropTypes.object,
  descriptionHtml: PropTypes.string,
};
